import React, { useState, useEffect } from 'react';
import { SxProps } from '@mui/material';
import { sendApiRequest } from 'lib/api/public';
import notify from 'lib/notify';
import { getTablePreferences, setTablePreferences } from 'components/utils';
import { ClientStatus } from 'components/client/types';
import StatusFilterSelector from 'components/common/StatusFilterSelector';
import {
  applySavedStatusFilterVisibility,
  getSavedStatusFilterVisibility,
  getVisibleStatuses,
} from 'components/common/utils';

const ClientStatusFilter = ({
  onChange,
  tableName = 'clients',
  layout = 'chip',
  sx,
  testId,
}: {
  onChange?: (value: string) => void;
  tableName?: string;
  layout?: 'combobox' | 'chip';
  sx?: SxProps;
  testId?: string;
}) => {
  const statusFilter = (getTablePreferences(tableName).statusFilter || '') as string;
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<ClientStatus[]>([]);

  useEffect(() => {
    void fetchStatuses();

    if (onChange) {
      onChange(statusFilter);
    }
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = (await sendApiRequest({
        path: '/clients/statuses',
        method: 'GET',
      })) as { list: ClientStatus[] };

      const statusesWithoutProspect = (response.list || []).filter(status => status.useForProspect !== true);
      const visibleStatuses = getVisibleStatuses(
        applySavedStatusFilterVisibility({
          statuses: statusesWithoutProspect,
          savedVisibility: getSavedStatusFilterVisibility(tableName),
        }),
      );

      setStatuses(visibleStatuses);
      setSelectedStatuses(
        statusFilter
          ? statusFilter.split(',').filter(id => visibleStatuses.some(status => status._id === id))
          : visibleStatuses.map(status => status._id),
      );
    } catch (error) {
      notify(error);
    }
  };

  const onChangeStatus = (newSelectedStatuses: string[]): void => {
    const allStatusIds = statuses.map(status => status._id);
    const normalizedSelectedStatuses =
      newSelectedStatuses.length === 0 || newSelectedStatuses.length === allStatusIds.length
        ? allStatusIds
        : newSelectedStatuses;

    setSelectedStatuses(normalizedSelectedStatuses);

    const newStatusFilter =
      normalizedSelectedStatuses.length === allStatusIds.length ? '' : normalizedSelectedStatuses.join(',');

    setTablePreferences({
      tableName,
      values: [
        {
          key: 'statusFilter',
          value: newStatusFilter,
        },
      ],
    });

    if (onChange) {
      onChange(newStatusFilter);
    }
  };

  return (
    <StatusFilterSelector
      statuses={statuses}
      selectedStatuses={selectedStatuses}
      onChangeStatus={onChangeStatus}
      layout={layout}
      sx={sx}
      testId={testId}
    />
  );
};

export default ClientStatusFilter;
